// Mirrors backend-services/common/src/types/extension.ts (dialer section).
export const DIALER_DISPOSITION_KEYS = [
  "NO_ANSWER",
  "BAD_NUMBER",
  "NO_NUMBER",
  "LEFT_VOICEMAIL",
  "DUPLICATE_TASK",
  "GATEKEEPER",
] as const;
export type DialerDispositionKey = (typeof DIALER_DISPOSITION_KEYS)[number];

export const DIALER_DISPOSITION_LABELS: Record<DialerDispositionKey, { title: string; hint: string }> = {
  NO_ANSWER: { title: "No answer", hint: "Logged when nobody picks up or the ring timeout hits." },
  LEFT_VOICEMAIL: { title: "Left voicemail", hint: "Logged after a voicemail is dropped." },
  GATEKEEPER: { title: "Gatekeeper", hint: "Logged when a gatekeeper answers instead of the prospect." },
  BAD_NUMBER: { title: "Bad number", hint: "Logged when the number is out of service." },
  NO_NUMBER: { title: "No number", hint: "Logged when the prospect has no phone number." },
  DUPLICATE_TASK: { title: "Duplicate task", hint: "Logged when the same call task appears twice." },
};

export type DialerVoicemailBehavior = "DROP" | "SKIP";

export interface DialerFlags {
  AUTOMATED_VOICEMAIL_FLOW: boolean;
  AUTOMATED_VOICEMAIL_BEHAVIOR: DialerVoicemailBehavior;
  AUTOMATED_GATEKEEPER_FLOW: boolean;
  NO_NUMBER_ENRICHMENT: boolean;
  EARLY_CALL_END_REDIAL: boolean;
  LOCK_HANGUP_WHILE_RINGING: boolean;
  RING_TIMEOUT_ENABLED: boolean;
  RING_TIMEOUT_SECONDS: number;
}

export interface DialerCallDelay {
  enabled: boolean;
  delayMs: number;
  adminLocked: boolean;
}

export interface DialerCallingHours {
  enabled: boolean;
  blockMode: boolean;
  minHour: number;
  minMinute: number;
  maxHour: number;
  maxMinute: number;
}

export interface DialerSettings {
  enabled: boolean;
  dispositions: Record<DialerDispositionKey, string>;
  flags: DialerFlags;
  callDelay: DialerCallDelay;
  callingHours: DialerCallingHours;
  availableDispositions?: string[];
  availableDispositionsSource?: "outreach_api" | "extension";
  availableDispositionsUpdatedAt?: number;
}

export interface DialerSettingsResult {
  settings: DialerSettings;
  outreachDispositions: string[] | null;
  organizationId: string;
  /** Plan gating: Dial faster needs Everything or the Dial faster add-on (Prospector on the legacy plans). */
  planRequired?: boolean;
}

/** A rep's own dialing preferences, laid over the organization's defaults. */
export interface MyDialerSettings {
  flags?: Partial<DialerSettings["flags"]>;
  callDelay?: Partial<DialerSettings["callDelay"]>;
  callingHours?: Partial<DialerSettings["callingHours"]>;
  updatedAt?: number;
}

export interface MyDialerResult {
  org: DialerSettings;
  mine: MyDialerSettings;
  effective: DialerSettings;
  /** Plan gating: Dial faster needs Everything or the Dial faster add-on (Prospector on the legacy plans). */
  planRequired?: boolean;
}
